import React from 'react';
import { Quote } from 'lucide-react';
import { SEO } from '../components/SEO';
import { Card, CardContent } from '../components/ui/card';

export const Testimonials = () => {
  const testimonials = [
    {
      name: 'Patient',
      condition: 'Varicose Veins Treatment, Mumbai',
      text: 'Dr. Punit Mahajan explained the laser treatment for my varicose veins very clearly. The procedure was simple with very little pain and I could walk the same day. My leg pain and swelling became much better within weeks. I would definitely recommend him to anyone suffering from varicose veins in Mumbai.',
      language: 'English'
    },
    {
      name: 'पेशंट',
      condition: 'Varicose Veins Treatment (Marathi), Mumbai',
      text: 'मला पायात सुज आलेल्या आणि वाकड्या शिरा होत्या. जास्त वेळ उभा राहिल्यावर पाय दुखायचे, जड वाटायचे आणि सुज यायची. मला surgery ची भीती होती, पण Dr. Punit Mahajan यांनी laser treatment खुप चांगल्या प्रकारे समजावुन सांगितली. Treatment नंतर माझा पायाचा pain आणि सुज खूप कमी झाली. आता मी आरामात चालू आणि उभा राहू शकतो.',
      language: 'Marathi'
    },
    {
      name: 'पेशंट',
      condition: 'Venous Ulcer / Diabetic Foot (Hindi), Mumbai',
      text: 'मेरे पैर में एक घाव था जो बहुत दिनों से भर नहीं रहा था. कई जगह इलाज करवाया लेकिन ज्यादा फायदा नहीं हुआ. फिर मैंने Dr. Punit Mahajan को दिखाया. उन्होंने बहुत आसान भाषा में समझाया कि यह venous ulcer है और इसका सही इलाज शुरू किया. इलाज के बाद धीरे-धीरे घाव भरने लगा, दर्द कम हो गया और अब मैं आराम से चल-फिर पा रहा हूं.',
      language: 'Hindi'
    },
    {
      name: 'Patient',
      condition: 'Varicose Veins Consultation, Mumbai',
      text: 'I visited Dr. Punit because I was worried about the veins in my legs. After examining me, Dr. Punit honestly explained that my condition was still in the early stage and could be managed without surgery for now. He advised lifestyle changes, stockings, exercise, and medicines instead of pushing me towards a procedure. My symptoms have improved a lot. It is rare to find a doctor who advises what is truly best for the patient.',
      language: 'English'
    },
    {
      name: 'Patient',
      condition: 'AV Fistula Salvage (Venoplasty), Mumbai',
      text: 'I am on regular dialysis and suddenly my fistula was not working properly. My hand had swelling and dialysis sessions were becoming very difficult. I was worried I may need a new fistula surgery. Dr. Mahajan explained that the fistula could be saved with a procedure called venoplasty. The procedure was done smoothly with very little discomfort. After treatment my fistula started working properly and dialysis became normal. The swelling also reduced. I felt very relieved that my fistula was saved without another surgery. Thank you Dr. Mahajan.',
      language: 'English'
    }
  ];

  return (
    <>
      <SEO 
        title="Patient Reviews - Dr. Punit Mahajan, Interventional Radiologist, Mumbai"
        description="Read genuine patient experiences with Dr. Punit Mahajan's varicose vein, dialysis access, and vascular treatments at Bombay Hospital and other Mumbai locations."
      />

      {/* Hero */}
      <section className="bg-gradient-to-br from-blue-950 to-slate-900 text-white py-20 relative overflow-hidden"><div className="absolute inset-0 pattern-hero opacity-20"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <Quote className="w-16 h-16 text-amber-500 mx-auto mb-6" />
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 animate-text-reveal">
            Patient Testimonials
          </h1>
          <p className="text-xl md:text-2xl text-slate-300 max-w-3xl mx-auto animate-text-reveal" style={{ animationDelay: "300ms" }}>
            What Patients Say About Dr. Punit Mahajan
          </p>
        </div>
      </section>

      {/* Intro */}
      <section className="py-16 bg-cream-textured relative overflow-hidden">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="gold-ornament mb-6">Real Stories</div>
          <p className="text-lg text-slate-600 leading-relaxed">
            Dr. Punit Mahajan's patients come from across Mumbai — Marine Lines, Chembur, Vashi, Vikhroli, Govandi, and beyond. Here is what they say:
          </p>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 bg-cream-stripes relative overflow-hidden">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="border-l-4 border-amber-500 hover:shadow-xl transition-shadow bg-white">
                <CardContent className="p-8">
                  <div className="flex items-start mb-4">
                    <Quote className="w-8 h-8 text-amber-600 mr-4 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-lg text-slate-700 leading-relaxed mb-6 italic">
                        "{testimonial.text}"
                      </p>
                      <div className="flex items-center justify-between flex-wrap gap-4">
                        <div>
                          <p className="font-semibold text-slate-900">— {testimonial.name}</p>
                          <p className="text-sm text-slate-600">{testimonial.condition}</p>
                        </div>
                        <span className="text-xs bg-amber-100 text-amber-800 px-3 py-1 rounded-full font-medium">
                          {testimonial.language}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-br from-blue-900 to-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <h2 className="text-3xl md:text-4xl font-bold">
            Experience Expert Interventional Radiology Care
          </h2>
          <p className="text-xl text-slate-300">
            Join hundreds of satisfied patients who have benefited from Dr. Punit Mahajan's expertise
          </p>
        </div>
      </section>
    </>
  );
};
